import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { DayPicker, type DropdownProps, type Matcher } from 'react-day-picker';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';
import { addYears, endOfYear, format, type Locale } from 'date-fns';
import { enGB, enUS, it, ptBR } from 'date-fns/locale';

import { ClickAwayListener, Popper, type ValueTypes } from '@myn/mui';

import CalendarButton from '../../common/Calendar/CalendarButton.jsx';
import { StyledDayPickerWrapper } from '../../common/Calendar/DayPickerWrapper.jsx';
import Box from '../Box/Box.jsx';
import Button from '../Button/Button.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Input from '../Input/Input.jsx';
import Select from '../Select/Select.jsx';
import type { TooltipProps } from '../Tooltip/Tooltip.jsx';

export interface DatePickerProps {
  /**
   * PROPS FOR THE INPUT
   */
  /**
   * Color of the datepicker input
   */
  color?: 'primary' | 'secondary';
  /**
   * Label for the datepicker input
   */
  label?: string;
  /**
   * If true, the datepicker input will be full width.
   */
  fullWidth?: boolean;
  /**
   * Helper text for the datepicker input
   */
  helperText?: string;
  /**
   * Size of the datepicker input
   */
  size?: 'small' | 'medium';
  /**
   * Text to be displayed below the datepicker input
   * */
  infoText?: string;
  /**
   * If true, the datepicker input will be disabled
   */
  disabled?: boolean;
  /**
   * If true, the datepicker input will be in error state
   */
  error?: boolean;
  /**
   * If true, the datepicker input will be in warning state
   */
  warning?: boolean;
  /**
   * If true, the datepicker input will be read-only
   */
  readOnly?: boolean;
  /**
   * Props for the tooltip component that displays the info text
   */
  InfoTextTooltipProps?: Partial<TooltipProps>;
  /**
   * The end adornment for the datepicker input
   */
  endAdornment?: React.ReactNode;
  /**
   * onFocus event handler for the input
   *   * @description Use `onApply` instead
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * onBlur event handler for the input
   *  * @description Use `onCancel` instead
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Prop that turn off 'close' icon button in input.
   * @default true
   */
  clearable?: boolean;
  /**
   * Callback that adds extra logic to `close` icon button in input.
   */
  onClear?: () => void;
  /**
   * PROPS FOR THE CALENDAR
   */
  /**
   * Additional class name for the datepicker
   */
  className?: string;
  /**
   * If true, the calendar will be opened
   * @default false
   * */
  isOpen?: boolean;
  /**
   * Array of dates that are events
   */
  eventDates?: Date[];
  /**
   * Selected date
   */
  selected?: Date | null;
  /**
   * Handler that fires when the datepickerRange closes
   */
  onApply?: (date?: Date | null) => void;
  /**
   * Placement of the calendar
   */
  placement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  /**
   * Helps to disable dates in various ways
   * Look at https://daypicker.dev/api/type-aliases/Matcher
   */
  disabledDates?: Matcher;
  /**
   * Props for the popper component that helps to control the position of the calendar (also works with Modal Dialog)
   **/
  disablePortal?: boolean;
  /** Callback that fires if user closed the datepicker without applying the selected date
   **/
  onCancel?: () => void;
}
const localeMap = new Map([
  ['en-GB', enGB],
  ['en-US', enUS],
  ['it-IT', it],
  ['pt-BR', ptBR],
]);

/**
 * @deprecated Use DatepickerInput and CalendarSingle from @mint-lib/tw-components/patterns instead
 */
const DatePicker = ({
  placement = 'bottom-start',
  clearable = true,
  ...props
}: DatePickerProps) => {
  const { t } = useTranslation('@myn/mui');
  const [localSelected, setLocalSelected] = useState<Date | undefined>(
    props.selected ?? undefined,
  );
  const popperMods = useMemo(
    () => [
      {
        name: 'flip',
        enabled: true,
        options: {
          altBoundary: false,
          rootBoundary: 'document',
        },
      },
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          altAxis: true,
          altBoundary: false,
          tether: false,
          rootBoundary: 'viewport',
        },
      },
    ],
    [],
  );

  const { getProfileField } = useProfile();
  const locale = getProfileField('locale');
  const formattingConfig =
    getProfileField('formattingConfig')?.datesFormat || locale;
  const localize = useMemo(
    () =>
      ({
        ...localeMap.get(locale),
        options: localeMap.get(formattingConfig)?.options,
        formatLong: localeMap.get(formattingConfig)?.formatLong,
      }) as Locale,
    [formattingConfig, locale],
  );

  const formatDateToString = useCallback(
    (date: Date | null | undefined) => {
      return date ? format(date, 'P', { locale: localize }) : undefined;
    },
    [localize],
  );
  const formattedDate = formatDateToString(localSelected);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(
    Boolean(props.isOpen),
  );

  const initialDate = formattedDate ?? '';

  const [outerInputValue, setOuterInputValue] = useState<string>(initialDate);
  const [selectMonth, setSelectMonth] = useState<Date | undefined>(
    props.selected ?? undefined,
  );
  // handler for applying the date
  const handleDateApply = (date: string) => {
    setOuterInputValue(date);
    setIsCalendarOpen(false);
  };

  // handler for opening the calendar
  const handleOpen = () => {
    setIsCalendarOpen((prev) => !prev);
    setLocalSelected(props.selected ?? undefined);
  };

  // handler for closing the calendar on click away
  const handleCancel = () => {
    setIsCalendarOpen(false);
    setLocalSelected(props.selected ?? undefined);
    props.onCancel?.();
  };

  // handler for clearing the date
  const handleClear = () => {
    setOuterInputValue('');
    setLocalSelected(props.selected ?? undefined);
    props.onApply?.(undefined);
    props.onClear?.();
  };

  // getting the enable dates
  const getEnabledMonth = (disableDates?: Matcher) => {
    if (
      typeof disableDates === 'object' &&
      ('after' in disableDates || 'from' in disableDates)
    ) {
      const enableMonth =
        'from' in disableDates ? disableDates.from : disableDates.after;
      return enableMonth;
    }
    return new Date();
  };
  const anchorEl = useRef(null);
  const popperId = useId();

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleCancel}>
      <Box aria-describedby={popperId}>
        <CalendarButton
          ref={anchorEl}
          variant="single"
          value={outerInputValue}
          infoText={props.infoText}
          disabledTextfield={props.disabled}
          InfoTextTooltipProps={props.InfoTextTooltipProps}
          warning={props.warning}
          color={props.color}
          label={props.label}
          fullWidth={props.fullWidth}
          error={props.error}
          size={props.size}
          endAdornment={
            clearable && !props.readOnly && outerInputValue !== '' ? (
              <IconButton
                data-testId="date-picker-clear-button"
                icon="Close"
                size="small"
                variant="icon"
                color="secondary"
                onClick={handleClear}
              />
            ) : null
          }
          helperText={!isCalendarOpen ? props.helperText : undefined}
          readOnlyTextfield={props.readOnly}
          onInputClick={() => {
            if (props.readOnly || props.disabled) {
              return;
            }
            handleOpen();
          }}
        />

        <Popper
          id={popperId}
          open={isCalendarOpen}
          //@ts-ignore => rethink the type of anchorEl
          anchorEl={() => anchorEl.current}
          placement={placement}
          disablePortal={props.disablePortal}
          modifiers={popperMods}
          sx={{
            zIndex: 2,
          }}
        >
          <StyledDayPickerWrapper variant="single" sidebar={false}>
            <DayPicker
              {...props}
              data-testId="date-picker-calendar"
              className={props.className}
              mode="single"
              month={selectMonth}
              defaultMonth={getEnabledMonth(props.disabledDates)}
              required
              hideNavigation
              onMonthChange={setSelectMonth}
              numberOfMonths={1}
              locale={localize}
              captionLayout="dropdown"
              endMonth={endOfYear(addYears(new Date(), 15))}
              formatters={{
                formatWeekdayName: (date) =>
                  format(date, 'ccccc', {
                    locale: localize,
                  }),
              }}
              disabled={props.disabledDates}
              selected={localSelected ?? undefined}
              onSelect={(date: Date) => {
                setLocalSelected(date);
              }}
              components={{
                Dropdown: DropdownSelect,
              }}
              modifiers={{
                eventDays: props.eventDates,
              }}
              modifiersClassNames={{
                eventDays: 'rdp-event-days_style',
              }}
              footer={
                <Box
                  sx={{
                    padding: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: (theme) =>
                      `1px solid ${theme.palette.border.secondary}`,
                    height: '48px',
                  }}
                >
                  <Box sx={{ width: '112px' }}>
                    <Input
                      data-testId="date-picker-footer-input"
                      size="small"
                      placeholder={t('Date')}
                      fullWidth
                      readOnly
                      value={initialDate}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleDateApply(formattedDate ?? '');
                      props.onApply?.(localSelected);
                    }}
                    disabled={!initialDate}
                  >
                    {t('Apply')}
                  </Button>
                </Box>
              }
            />
          </StyledDayPickerWrapper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default DatePicker;

const DropdownSelect = (dropdownProps: DropdownProps) => {
  const monthsInYear = 12;
  const isMonthDropdown = dropdownProps.options?.length === monthsInYear;
  const options = isMonthDropdown
    ? dropdownProps.options
    : dropdownProps.options?.toReversed();
  const formattedOptions = useMemo(() => {
    return options?.map((option) => ({
      value: option.value,
      label: option.label.charAt(0).toUpperCase() + option.label.slice(1),
    }));
  }, [options]);
  const handleDropdownChange = (e: any) => {
    dropdownProps.onChange?.(e);
  };

  return (
    <Select
      value={dropdownProps.value as ValueTypes}
      onChangeNative={handleDropdownChange}
      fullWidth
      size="small"
      options={formattedOptions ?? []}
      selectedMenuVariant
    />
  );
};
